import { useEffect, useState } from "react";
import {
    DocumentDuplicateIcon,
    DocumentIcon,
} from "@heroicons/react/24/outline";
import useApi from "utils/use-api";
import { classNames } from "utils";
import { fetchPlacingSlipBlobUrl } from "./placing-slip-api";

const isPdfFile = (file: File) =>
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf");

const getFileExtension = (name: string) =>
    name.split(".").pop()?.toUpperCase() || "FILE";

const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const withPdfFitView = (url: string) =>
    `${url}#page=1&view=Fit&toolbar=0&navpanes=0&scrollbar=0`;

type Props = {
    file?: File | null;
    previewUrl?: string | null;
    title?: string;
    subtitle?: string;
    className?: string;
    compact?: boolean;
};

const PlacingSlipFilePreview = ({
    file,
    previewUrl: externalPreviewUrl,
    title,
    subtitle,
    className,
    compact = true,
}: Props) => {
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!file || !isPdfFile(file)) {
            setLocalPreviewUrl(null);
            return;
        }

        const url = URL.createObjectURL(file);
        setLocalPreviewUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [file]);

    const resolvedPreviewUrl = localPreviewUrl || externalPreviewUrl || null;
    const showPdfPreview =
        resolvedPreviewUrl &&
        ((file && isPdfFile(file)) || Boolean(externalPreviewUrl));

    if (showPdfPreview && resolvedPreviewUrl) {
        return (
            <div
                className={classNames(
                    "shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm",
                    compact ? "h-28 w-20" : "h-56 w-40",
                    className || "",
                )}>
                <iframe
                    src={withPdfFitView(resolvedPreviewUrl)}
                    title="Placing slip preview"
                    className="pointer-events-none h-full w-full border-0 bg-white"
                />
            </div>
        );
    }

    const displayName = file?.name || title || "Placing Slip";
    const extension = file ? getFileExtension(file.name) : "PDF";

    return (
        <div
            className={classNames(
                "flex shrink-0 flex-col items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-center shadow-sm",
                compact ? "h-28 w-20 px-1 py-2" : "h-56 w-40 px-3 py-4",
                className || "",
            )}>
            {extension === "PDF" ? (
                <DocumentIcon
                    className={classNames(
                        "text-primary-600",
                        compact ? "h-7 w-7" : "h-10 w-10",
                    )}
                />
            ) : (
                <DocumentDuplicateIcon
                    className={classNames(
                        "text-primary-600",
                        compact ? "h-7 w-7" : "h-10 w-10",
                    )}
                />
            )}
            <p
                className={classNames(
                    "mt-1 w-full break-all font-medium text-gray-800",
                    compact ? "text-[10px] leading-tight" : "text-xs",
                )}>
                {displayName}
            </p>
            {!compact && subtitle && (
                <p className="mt-1 text-[10px] text-gray-500">{subtitle}</p>
            )}
        </div>
    );
};

export function useExistingPlacingSlipPreviewUrl(
    offerId: string,
    enabled: boolean,
) {
    const api = useApi();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;

        if (!enabled || !offerId) {
            setPreviewUrl((prev) => {
                if (prev) {
                    URL.revokeObjectURL(prev);
                }
                return null;
            });
            setLoading(false);
            return;
        }

        const loadPreview = async () => {
            setLoading(true);

            try {
                const blobUrl = await fetchPlacingSlipBlobUrl(api, offerId);
                if (!active) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                setPreviewUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return blobUrl;
                });
            } catch {
                if (active) {
                    setPreviewUrl((prev) => {
                        if (prev) {
                            URL.revokeObjectURL(prev);
                        }
                        return null;
                    });
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            active = false;
        };
    }, [offerId, enabled]);

    return { previewUrl, loading };
}

export { formatFileSize, getFileExtension, isPdfFile };
export default PlacingSlipFilePreview;
